import { useUserStore } from '../../stores/prioritization/userStore';
import { isAdminRole } from '../../types/prioritization/roles';

/**
 * Hook for checking user permissions across the system.
 * Delegates to the capability-based userStore.can() for all checks.
 */
export function usePermissions() {
  const currentRole = useUserStore((state) => state.currentRole);
  const can = useUserStore((state) => state.can);
  const canAccessSpace = useUserStore((state) => state.canAccessSpace);
  const canAdmin = useUserStore((state) => state.canAdmin);

  // isAdminRole includes workspace-admin, tenant-admin, platform-admin, and system-admin.
  // Previously used isPlatformOrSystemAdmin which excluded workspace-admin, causing workspace
  // admins to lose canViewAnalytics and canCoordinateLists.
  const isAdmin = currentRole ? isAdminRole(currentRole) : false;

  return {
    currentRole,
    /** @deprecated Tier is no longer used. */
    currentTier: 1,
    /** @deprecated Use can() checks instead. */
    capabilities: {
      canView: true,
      canEdit: can('items.edit'),
      canCreate: can('items.create'),
      canPublish: can('lists.publish'),
      canAdmin: isAdmin,
    },

    canAccessSpace,
    canEditSpace: (_spaceId?: string) => can('items.edit'),

    canCreateEntity: can('items.create'),
    canPublishList: can('lists.publish'),
    canAdminSystem: canAdmin(),

    canManageAttributes: can('criteria.edit') || isAdmin,
    canDefineCriteria: can('criteria.create'),
    canRankPriorities: can('lists.edit'),
    canMoveCutLine: can('lists.move_cutline'),
    canShapeCandidates: can('items.edit') || isAdmin,
    canFacilitateSessions: can('session.facilitate'),
    canDesignEvents: can('event.design') || can('session.facilitate') || isAdmin,
    canApplyOverride: can('ranking.override') || isAdmin,
    canViewAnalytics: can('analytics.view'),
    canCoordinateLists: can('lists.publish'),
    canCreateFocusLists: can('lists.create'),

    hasCapability: (capability: string) => {
      const capMap: Record<string, boolean> = {
        canView: true,
        canEdit: can('items.edit'),
        canCreate: can('items.create'),
        canPublish: can('lists.publish'),
        canAdmin: isAdmin,
      };
      return capMap[capability] ?? false;
    },

    can: (action: string): boolean => {
      switch (action) {
        case 'items.edit':
          return can('items.edit') || isAdmin;
        case 'items.create':
          return can('items.create') || isAdmin;
        case 'items.delete':
          return can('items.delete') || isAdmin;
        case 'process.prioritize':
          return can('lists.publish') || isAdmin;
        case 'process.facilitate':
          return can('session.facilitate') || isAdmin;
        case 'admin.manage':
          return isAdmin;
        default:
          return false;
      }
    },
  };
}
